import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';

// Role types
export type UserRole = 'owner' | 'admin' | 'mechanic' | 'member' | null;

// Permission keys
export type Permission =
    | 'dashboard'
    | 'workshop'
    | 'invoices'
    | 'expenses'
    | 'financial_reports'
    | 'team_management'
    | 'settings'
    | 'billing'
    | 'bookings'
    | 'edit_work';

/**
 * Permission matrix.
 *
 * This mirrors the RLS role matrix enforced in the database (migrations
 * 20260802004410 / 004557 / 005332). It is a UX layer only — hiding a menu is
 * not a security control, and the database denies the same actions regardless
 * of what this file says. Keep the two in step: if you widen something here
 * without widening RLS, the UI will offer an action that then fails.
 *
 *   owner     Everything.
 *   admin     Accountant. The books, and only the books: P&L, expenses,
 *             invoices, supplier accounts. Not the workshop floor — no jobs,
 *             appointments, schedule or client records. Writes nothing.
 *   mechanic  All work. No financial visibility, no org settings.
 *   member    Front desk. Bookings and customer registration only.
 */
const PERMISSIONS: Record<Permission, UserRole[]> = {
    // The dashboard headline is Safe-to-Spend, so it is financial by nature.
    // fn_get_financial_snapshot returns a 'forbidden' payload to the other roles.
    // The accountant is deliberately NOT here: they land on Business Health
    // instead, which is the same numbers without the workshop furniture.
    dashboard: ['owner'],
    workshop: ['owner', 'mechanic', 'member'],            // the floor: jobs, clients, schedule
    invoices: ['owner', 'admin', 'mechanic', 'member'],   // revenue — the accountant needs this
    edit_work: ['owner', 'mechanic'],                     // creating/pricing it
    bookings: ['owner', 'mechanic', 'member'],            // appointments + customers
    expenses: ['owner', 'admin', 'mechanic'],             // receipts in, costs out
    financial_reports: ['owner', 'admin'],                // P&L + supplier accounts
    team_management: ['owner'],
    settings: ['owner', 'admin', 'mechanic', 'member'],   // own profile only
    billing: ['owner'],
};

// landingPathForRole lives in ../lib/roleLanding — RoleContext exports the
// RoleProvider component, and react-refresh/only-export-components (ratcheted
// in CI) objects to mixing component and non-component exports in one module.

interface RoleContextType {
    role: UserRole;
    loading: boolean;
    can: (permission: Permission) => boolean;
    isOwner: boolean;
    isAdmin: boolean;
    isMechanic: boolean;
    refetchRole: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider = ({ children }: { children: ReactNode }) => {
    const [role, setRole] = useState<UserRole>(null);
    const [loading, setLoading] = useState(true);

    const fetchRole = async () => {
        try {
            const { data, error } = await supabase.rpc('fn_get_user_role');

            if (error) {
                console.error('Error fetching role:', error);
                setRole(null);
            } else {
                setRole(data as UserRole);
            }
        } catch (err) {
            console.error('Role fetch error:', err);
            setRole(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRole();

        // Re-fetch on auth state change
        const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
            fetchRole();
        });

        return () => subscription.unsubscribe();
    }, []);

    // Permission check function
    const can = (permission: Permission): boolean => {
        if (!role) return false;
        return PERMISSIONS[permission]?.includes(role) ?? false;
    };

    const value: RoleContextType = {
        role,
        loading,
        can,
        isOwner: role === 'owner',
        isAdmin: role === 'admin',
        isMechanic: role === 'mechanic',
        refetchRole: fetchRole,
    };

    return (
        <RoleContext.Provider value={value}>
            {children}
        </RoleContext.Provider>
    );
};

export const useRole = (): RoleContextType => {
    const context = useContext(RoleContext);
    if (context === undefined) {
        throw new Error('useRole must be used within a RoleProvider');
    }
    return context;
};
